import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config/GameConfig';
import type Difficulty from '../systems/Difficulty';

interface ParallaxStar extends Phaser.GameObjects.Image {
  _speed: number;
  _drift: number;
}

interface EmberSpeck extends Phaser.GameObjects.Arc {
  _speed: number;
  _drift: number;
}

interface BattlefieldObstacle extends Phaser.Physics.Arcade.Image {
  _speedMul: number;
}

/**
 * Background —— 滚动背景 + 战场装饰
 *
 * 焦土地面 TileSprite 滚动速度跟当前难度的 fallSpeed 同步（×0.85），
 * 让“地面后退”的速度感与敌人下落一致。障碍物使用动态 immovable body，随背景滚动且能阻挡角色/子弹。
 */
export default class Background {
  scene: Phaser.Scene;
  difficulty: Difficulty;

  private _grass: Phaser.GameObjects.TileSprite;
  private _stars: ParallaxStar[] = [];
  private _embers: EmberSpeck[] = [];
  private _obstacles: BattlefieldObstacle[] = [];

  constructor(scene: Phaser.Scene, difficulty: Difficulty, obstacleGroup?: Phaser.Physics.Arcade.Group) {
    this.scene = scene;
    this.difficulty = difficulty;

    // 战场底色：焦土地面 + 上方烟雾/警戒光，保持可读性，不压暗怪物。
    scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x243026, 1).setOrigin(0, 0).setDepth(-20);
    scene.add.circle(GAME_WIDTH * 0.18, GAME_HEIGHT * 0.18, 190, 0x586a54, 0.18).setDepth(-19);
    scene.add.circle(GAME_WIDTH * 0.82, GAME_HEIGHT * 0.26, 220, 0x604128, 0.16).setDepth(-19);
    scene.add.rectangle(GAME_WIDTH / 2, 0, GAME_WIDTH, GAME_HEIGHT, 0xffffff, 0.04).setOrigin(0.5, 0).setDepth(-11);

    // 焦土地面铺满整个屏幕
    this._grass = scene.add
      .tileSprite(0, 0, GAME_WIDTH, GAME_HEIGHT, 'grass-tile')
      .setOrigin(0, 0)
      .setDepth(-15)
      .setAlpha(0.82);

    // 两侧淡淡的战壕/路肩线，给自由战场一个纵深方向
    scene.add.rectangle(34, 0, 3, GAME_HEIGHT, 0x0a0c09, 0.24).setOrigin(0, 0).setDepth(-10);
    scene.add.rectangle(GAME_WIDTH - 37, 0, 3, GAME_HEIGHT, 0x0a0c09, 0.24).setOrigin(0, 0).setDepth(-10);
    scene.add.rectangle(42, 0, 1, GAME_HEIGHT, 0xff8a3c, 0.12).setOrigin(0, 0).setDepth(-9);
    scene.add.rectangle(GAME_WIDTH - 43, 0, 1, GAME_HEIGHT, 0xff8a3c, 0.12).setOrigin(0, 0).setDepth(-9);

    const layers = [
      { count: 18, speed: 18, alpha: 0.14, scale: 2.2 },
      { count: 10, speed: 42, alpha: 0.24, scale: 3.2 },
    ];
    for (const cfg of layers) {
      for (let i = 0; i < cfg.count; i++) {
        const star = scene.add
          .image(
            Phaser.Math.Between(8, GAME_WIDTH - 8),
            Phaser.Math.Between(0, GAME_HEIGHT),
            'star',
          )
          .setTint(0x6f6a58)
          .setAlpha(cfg.alpha)
          .setScale(cfg.scale)
          .setDepth(-12) as ParallaxStar;
        star._speed = cfg.speed;
        star._drift = Phaser.Math.Between(-8, 8);
        this._stars.push(star);
      }
    }

    for (let i = 0; i < 12; i++) {
      const ember = scene.add
        .circle(
          Phaser.Math.Between(16, GAME_WIDTH - 16),
          Phaser.Math.Between(0, GAME_HEIGHT),
          Phaser.Math.FloatBetween(0.8, 1.8),
          0xff8a3c,
          Phaser.Math.FloatBetween(0.18, 0.42),
        )
        .setDepth(-8) as EmberSpeck;
      ember._speed = Phaser.Math.Between(18, 46);
      ember._drift = Phaser.Math.Between(-12, 12);
      this._embers.push(ember);
    }

    this._buildObstacles(obstacleGroup);
  }

  /** 动态障碍物：随背景向下滚动，使用 immovable body 避免被玩家/怪物推开 */
  private _buildObstacles(obstacleGroup?: Phaser.Physics.Arcade.Group): void {
    const keys = ['obstacle-sandbag', 'obstacle-rubble', 'obstacle-barricade'];
    const spots = [
      { x: 70, y: 170 },
      { x: 350, y: 250 },
      { x: 120, y: 410 },
      { x: 330, y: 520 },
      { x: 78, y: 660 },
      { x: 372, y: 735 },
    ];
    for (let i = 0; i < spots.length; i++) {
      const o = obstacleGroup
        ? obstacleGroup.create(spots[i].x, spots[i].y, keys[i % keys.length]) as BattlefieldObstacle
        : this.scene.physics.add.image(spots[i].x, spots[i].y, keys[i % keys.length]) as BattlefieldObstacle;
      o.setAlpha(0.82)
        .setDepth(-7)
        .setScale(i % 2 === 0 ? 0.9 : 0.78)
        .setAngle(i % 3 === 0 ? -8 : (i % 3 === 1 ? 6 : 0));
      const body = o.body as Phaser.Physics.Arcade.Body;
      body.setAllowGravity(false);
      body.setImmovable(true);
      body.setVelocity(0, 0);
      // 碰撞体略小于视觉，避免贴边时“看起来没碰到却被卡住”。
      body.setSize(o.width * 0.62, o.height * 0.48);
      body.setOffset(o.width * 0.19, o.height * 0.36);
      o._speedMul = 0.72 + (i % 3) * 0.1;
      this._obstacles.push(o);
    }
  }

  update(delta: number): void {
    const dt = delta / 1000;
    const grassSpeed = this.difficulty.fallSpeed * 0.85;
    this._grass.tilePositionY -= grassSpeed * dt;

    for (const s of this._stars) {
      s.y += s._speed * dt;
      s.x += s._drift * dt;
      if (s.y > GAME_HEIGHT + 4 || s.x < -4 || s.x > GAME_WIDTH + 4) {
        s.y = -4;
        s.x = Phaser.Math.Between(8, GAME_WIDTH - 8);
      }
    }

    for (const e of this._embers) {
      e.y -= e._speed * dt;
      e.x += e._drift * dt;
      if (e.y < -4 || e.x < -4 || e.x > GAME_WIDTH + 4) {
        e.y = GAME_HEIGHT + 4;
        e.x = Phaser.Math.Between(16, GAME_WIDTH - 16);
      }
    }

    const obstacleSpeed = grassSpeed * 0.72;
    for (const o of this._obstacles) {
      const body = o.body as Phaser.Physics.Arcade.Body;
      const vy = obstacleSpeed * o._speedMul;
      o.y += vy * dt;
      body.updateFromGameObject();
      if (o.y > GAME_HEIGHT + 44) {
        o.y = -44;
        o.x = Phaser.Math.Between(56, GAME_WIDTH - 56);
        o.setTexture(['obstacle-sandbag', 'obstacle-rubble', 'obstacle-barricade'][Phaser.Math.Between(0, 2)]);
        o.setAngle(Phaser.Math.Between(-10, 10));
        body.setSize(o.width * 0.62, o.height * 0.48);
        body.setOffset(o.width * 0.19, o.height * 0.36);
        body.updateFromGameObject();
      }
    }
  }
}
